import { z } from "zod";

// ── Sync Status ──

export const SyncStatusSchema = z.enum([
  "backlog",
  "todo",
  "in_progress",
  "review",
  "done",
  "cancelled",
]);

export type SyncStatus = z.infer<typeof SyncStatusSchema>;

// ── Sync Item (what we push to the external tool) ──

export const SyncItemSchema = z.object({
  sliceId: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  complexity: z.string(),
  priority: z.string(),
  estimatedTokens: z.number(),
  estimatedCost: z.number(),
  acceptanceCriteria: z.string(),
  labels: z.array(z.string()),
});

export type SyncItem = z.infer<typeof SyncItemSchema>;

// ── Sync Result (returned after creating an item) ──

export interface SyncResult {
  externalId: string;
  url: string;
  provider: string;
}

// ── Synced Item (ledger entry) ──

export interface SyncedItem {
  sliceId: string;
  externalId: string;
  url: string;
  status: SyncStatus;
  lastSynced: number;
}

// ── Status Update (returned from pullStatus) ──

export interface StatusUpdate {
  sliceId: string;
  externalId: string;
  previousStatus: SyncStatus;
  newStatus: SyncStatus;
}

// ── Sync Config ──

export const SyncConfigSchema = z.object({
  provider: z.enum(["linear", "github", "notion", "jira"]),
  projectKey: z.string(),
  apiKey: z.string().optional(),
  apiKeyEnv: z.string().optional(),
  statusMapping: z.record(z.string(), SyncStatusSchema).optional(),
});

export type SyncConfig = z.infer<typeof SyncConfigSchema>;

// ── Sync Provider Interface ──

export interface SyncProvider {
  name: string;
  /** Sync a spec slice as an issue/task in the external tool */
  createItem(item: SyncItem): Promise<SyncResult>;
  /** Update status of an existing synced item */
  updateStatus(externalId: string, status: SyncStatus): Promise<void>;
  /** Get status of a synced item from the external tool */
  getStatus(externalId: string): Promise<SyncStatus>;
  /** List all synced items */
  listItems(projectKey: string): Promise<SyncedItem[]>;
}

// ── Sync Manager Interface ──

export interface SyncManager {
  /** Sync all pending slices to the external tool */
  syncSlices(slices: SyncItem[]): Promise<SyncResult[]>;
  /** Pull status updates from external tool back to Fathom */
  pullStatus(): Promise<StatusUpdate[]>;
  /** Get the sync ledger (what's synced where) */
  getLedger(): Promise<SyncedItem[]>;
}

// ── Ledger File Format ──

export interface SyncLedger {
  provider: string;
  projectKey: string;
  items: SyncedItem[];
  lastSync: number;
}
